/**
 * SwimFresh™ — site interactions
 * Vanilla JS: nav, sticky header, FAQ, reviews, qty, forms, reveals, motion
 */

(function () {
  "use strict";

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  /* ----------------------------------------------------------------------
     Scroll progress bar
     ---------------------------------------------------------------------- */
  const progressBar = document.createElement("div");
  progressBar.className = "scroll-progress";
  progressBar.setAttribute("aria-hidden", "true");
  if (document.body) { document.body.prepend(progressBar); }

  function updateProgress() {
    const doc = document.documentElement;
    const scrollTop = window.scrollY || doc.scrollTop;
    const height = doc.scrollHeight - doc.clientHeight;
    const pct = height > 0 ? (scrollTop / height) * 100 : 0;
    progressBar.style.width = pct + "%";
  }

  /* ----------------------------------------------------------------------
     Hero slider (4 slides)
     ---------------------------------------------------------------------- */
  const heroRoot = document.querySelector("[data-hero-slider]");
  if (heroRoot) {
    const slides = Array.from(heroRoot.querySelectorAll("[data-hero-slide]"));
    const dotsWrap = heroRoot.querySelector("[data-hero-dots]");
    const prevBtn = heroRoot.querySelector("[data-hero-prev]");
    const nextBtn = heroRoot.querySelector("[data-hero-next]");
    const progressBar = heroRoot.querySelector("[data-hero-progress]");
    let index = 0;
    let timer = null;
    let progressRaf = null;
    let slideStarted = 0;
    const SLIDE_MS = 6000;

    function loadSlideImage(slide) {
      if (!slide) return;
      const img = slide.querySelector("img[data-src]");
      if (!img) return;
      img.src = img.getAttribute("data-src");
      img.removeAttribute("data-src");
    }

    function setSlide(i) {
      if (!slides.length) return;
      index = (i + slides.length) % slides.length;
      loadSlideImage(slides[index]);
      slides.forEach(function (slide, si) {
        const on = si === index;
        slide.classList.toggle("is-active", on);
        slide.setAttribute("aria-hidden", on ? "false" : "true");
      });
      if (dotsWrap) {
        dotsWrap.querySelectorAll(".hero-dot").forEach(function (dot, di) {
          dot.classList.toggle("is-active", di === index);
          dot.setAttribute("aria-selected", di === index ? "true" : "false");
        });
      }
      restartTimer();
    }

    function tickProgress() {
      if (!progressBar || prefersReducedMotion) return;
      const elapsed = Date.now() - slideStarted;
      const pct = Math.min(100, (elapsed / SLIDE_MS) * 100);
      progressBar.style.width = pct + "%";
      if (pct < 100) {
        progressRaf = window.requestAnimationFrame(tickProgress);
      }
    }

    function restartTimer() {
      if (timer) window.clearInterval(timer);
      if (progressRaf) window.cancelAnimationFrame(progressRaf);
      slideStarted = Date.now();
      if (progressBar) progressBar.style.width = "0%";
      if (prefersReducedMotion || slides.length < 2) return;
      progressRaf = window.requestAnimationFrame(tickProgress);
      timer = window.setInterval(function () {
        setSlide(index + 1);
      }, SLIDE_MS);
    }

    if (dotsWrap && slides.length) {
      slides.forEach(function (_, i) {
        const dot = document.createElement("button");
        dot.type = "button";
        dot.className = "hero-dot" + (i === 0 ? " is-active" : "");
        dot.setAttribute("role", "tab");
        dot.setAttribute("aria-label", "Go to slide " + (i + 1));
        dot.setAttribute("aria-selected", i === 0 ? "true" : "false");
        dot.addEventListener("click", function () {
          setSlide(i);
        });
        dotsWrap.appendChild(dot);
      });
    }

    if (prevBtn) {
      prevBtn.addEventListener("click", function () {
        setSlide(index - 1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener("click", function () {
        setSlide(index + 1);
      });
    }

    // Pause on hover / focus
    heroRoot.addEventListener("mouseenter", function () {
      if (timer) window.clearInterval(timer);
      if (progressRaf) window.cancelAnimationFrame(progressRaf);
    });
    heroRoot.addEventListener("mouseleave", restartTimer);
    heroRoot.addEventListener("focusin", function () {
      if (timer) window.clearInterval(timer);
      if (progressRaf) window.cancelAnimationFrame(progressRaf);
    });
    heroRoot.addEventListener("focusout", function (e) {
      if (!heroRoot.contains(e.relatedTarget)) restartTimer();
    });

    // Keyboard
    heroRoot.addEventListener("keydown", function (e) {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setSlide(index - 1);
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        setSlide(index + 1);
      }
    });

    // Touch swipe
    let touchX = null;
    heroRoot.addEventListener(
      "touchstart",
      function (e) {
        touchX = e.changedTouches[0].screenX;
      },
      { passive: true }
    );
    heroRoot.addEventListener(
      "touchend",
      function (e) {
        if (touchX === null) return;
        const dx = e.changedTouches[0].screenX - touchX;
        if (Math.abs(dx) > 50) {
          setSlide(index + (dx < 0 ? 1 : -1));
        }
        touchX = null;
      },
      { passive: true }
    );

    setSlide(0);
  }

  /* ----------------------------------------------------------------------
     Sticky header
     ---------------------------------------------------------------------- */
  const header = document.getElementById("site-header");

  function updateHeader() {
    if (!header) return;
    if (window.scrollY > 40) {
      header.classList.add("is-scrolled");
    } else {
      header.classList.remove("is-scrolled");
    }
  }

  function onScroll() {
    updateHeader();
    updateProgress();
  }

  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll();

  /* ----------------------------------------------------------------------
     Mobile navigation
     ---------------------------------------------------------------------- */
  const menuToggle = document.getElementById("menu-toggle");
  const navMobile = document.getElementById("nav-mobile");

  function openMenu() {
    if (!navMobile || !menuToggle) return;
    if (navMobile.parentElement !== document.body) {
      document.body.appendChild(navMobile);
    }
    navMobile.removeAttribute("hidden");
    navMobile.hidden = false;
    void navMobile.offsetWidth;
    navMobile.classList.add("is-open");
    menuToggle.setAttribute("aria-expanded", "true");
    menuToggle.setAttribute("aria-label", "Close menu");
    document.body.classList.add("nav-open");
  }

  function closeMenu() {
    if (!navMobile || !menuToggle) return;
    navMobile.classList.remove("is-open");
    menuToggle.setAttribute("aria-expanded", "false");
    menuToggle.setAttribute("aria-label", "Open menu");
    document.body.classList.remove("nav-open");
    window.setTimeout(function () {
      if (!navMobile.classList.contains("is-open")) {
        navMobile.setAttribute("hidden", "");
        navMobile.hidden = true;
      }
    }, 400);
  }

  function toggleMenu(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!navMobile || !menuToggle) return;
    if (navMobile.classList.contains("is-open")) {
      closeMenu();
    } else {
      openMenu();
    }
  }

  if (menuToggle && navMobile && menuToggle.getAttribute("data-sf-menu-bound") !== "1") {
    menuToggle.setAttribute("data-sf-menu-bound", "1");
    menuToggle.addEventListener("click", toggleMenu);
    const closeBtn = document.getElementById("nav-mobile-close");
    if (closeBtn) {
      closeBtn.addEventListener("click", function (e) {
        e.preventDefault();
        closeMenu();
        menuToggle.focus();
      });
    }

    navMobile.addEventListener("click", function (e) {
      if (e.target === navMobile) closeMenu();
      const link = e.target.closest ? e.target.closest("a") : null;
      if (link) closeMenu();
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && navMobile.classList.contains("is-open")) {
        closeMenu();
        menuToggle.focus();
      }
    });
  }

  /* ----------------------------------------------------------------------
     Smooth scroll + active nav (native CSS handles most; enhance focus)
     ---------------------------------------------------------------------- */
  document.querySelectorAll('a[href^="#"]').forEach(function (anchor) {
    anchor.addEventListener("click", function (e) {
      const id = anchor.getAttribute("href");
      if (!id || id === "#") return;
      const target = document.querySelector(id);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "start",
      });
      // move focus for a11y
      if (!target.hasAttribute("tabindex")) {
        target.setAttribute("tabindex", "-1");
      }
      target.focus({ preventScroll: true });
    });
  });

  /* ----------------------------------------------------------------------
     Quantity controls
     ---------------------------------------------------------------------- */
  document.querySelectorAll("[data-qty]").forEach(function (wrap) {
    const input = wrap.querySelector("[data-qty-input]");
    const minus = wrap.querySelector("[data-qty-minus]");
    const plus = wrap.querySelector("[data-qty-plus]");
    if (!input) return;

    function clamp() {
      let v = parseInt(input.value, 10);
      if (isNaN(v) || v < 1) v = 1;
      const max = parseInt(input.getAttribute("max") || "99", 10);
      if (v > max) v = max;
      input.value = String(v);
    }

    if (minus) {
      minus.addEventListener("click", function () {
        input.value = String(Math.max(1, parseInt(input.value, 10) - 1 || 1));
        clamp();
      });
    }
    if (plus) {
      plus.addEventListener("click", function () {
        input.value = String((parseInt(input.value, 10) || 1) + 1);
        clamp();
      });
    }
    input.addEventListener("change", clamp);
  });

  /* Add to basket placeholder */
  const addBtn = document.getElementById("add-to-basket");
  const basketFeedback = document.getElementById("basket-feedback");
  if (addBtn && basketFeedback) {
    addBtn.addEventListener("click", function () {
      const qtyInput = document.querySelector("[data-qty-input]");
      const qty = qtyInput ? qtyInput.value : "1";
      basketFeedback.hidden = false;
      basketFeedback.textContent =
        "Added " +
        qty +
        " × SwimFresh™ spray to basket (demo — connect to Shopify/checkout later).";
      addBtn.textContent = "Added ✓";
      window.setTimeout(function () {
        addBtn.textContent = "Add to Basket";
      }, 2000);
    });
  }

  /* ----------------------------------------------------------------------
     FAQ accordion (accessible)
     ---------------------------------------------------------------------- */
  const faqRoot = document.querySelector("[data-faq]");
  if (faqRoot) {
    const items = faqRoot.querySelectorAll(".faq-item");

    items.forEach(function (item) {
      const btn = item.querySelector(".faq-item__button");
      if (!btn) return;

      btn.addEventListener("click", function () {
        const isOpen = item.classList.contains("is-open");

        // close others (single-open pattern)
        items.forEach(function (other) {
          other.classList.remove("is-open");
          const ob = other.querySelector(".faq-item__button");
          if (ob) ob.setAttribute("aria-expanded", "false");
        });

        if (!isOpen) {
          item.classList.add("is-open");
          btn.setAttribute("aria-expanded", "true");
        }
      });
    });
  }

  /* ----------------------------------------------------------------------
     Reviews slider
     ---------------------------------------------------------------------- */
  const reviewsRoot = document.querySelector("[data-reviews]");
  if (reviewsRoot) {
    const track = reviewsRoot.querySelector("[data-reviews-track]");
    const slides = track ? Array.from(track.children) : [];
    const dotsWrap = reviewsRoot.querySelector("[data-reviews-dots]");
    const prevBtn = reviewsRoot.querySelector("[data-reviews-prev]");
    const nextBtn = reviewsRoot.querySelector("[data-reviews-next]");
    let index = 0;
    let autoTimer = null;

    function goTo(i) {
      if (!slides.length) return;
      index = (i + slides.length) % slides.length;
      if (track) {
        track.style.transform = "translateX(-" + index * 100 + "%)";
      }
      if (dotsWrap) {
        dotsWrap.querySelectorAll(".reviews-dot").forEach(function (dot, di) {
          dot.classList.toggle("is-active", di === index);
          dot.setAttribute("aria-selected", di === index ? "true" : "false");
        });
      }
    }

    if (dotsWrap && slides.length) {
      slides.forEach(function (_, i) {
        const dot = document.createElement("button");
        dot.type = "button";
        dot.className = "reviews-dot" + (i === 0 ? " is-active" : "");
        dot.setAttribute("role", "tab");
        dot.setAttribute("aria-label", "Show review " + (i + 1));
        dot.setAttribute("aria-selected", i === 0 ? "true" : "false");
        dot.addEventListener("click", function () {
          goTo(i);
          restartAuto();
        });
        dotsWrap.appendChild(dot);
      });
    }

    if (prevBtn) {
      prevBtn.addEventListener("click", function () {
        goTo(index - 1);
        restartAuto();
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener("click", function () {
        goTo(index + 1);
        restartAuto();
      });
    }

    function restartAuto() {
      if (prefersReducedMotion || slides.length < 2) return;
      if (autoTimer) window.clearInterval(autoTimer);
      autoTimer = window.setInterval(function () {
        goTo(index + 1);
      }, 6000);
    }

    // pause on hover / focus
    reviewsRoot.addEventListener("mouseenter", function () {
      if (autoTimer) window.clearInterval(autoTimer);
    });
    reviewsRoot.addEventListener("mouseleave", restartAuto);
    reviewsRoot.addEventListener("focusin", function () {
      if (autoTimer) window.clearInterval(autoTimer);
    });
    reviewsRoot.addEventListener("focusout", restartAuto);

    goTo(0);
    restartAuto();
  }

  /* ----------------------------------------------------------------------
     Reveal on scroll
     ---------------------------------------------------------------------- */
  const revealEls = document.querySelectorAll(".reveal");
  // Mobile / reduced motion: show everything immediately (opacity:0 looked like broken layout)
  var forceReveal =
    prefersReducedMotion ||
    (window.matchMedia && window.matchMedia("(max-width: 1024px)").matches) ||
    (window.matchMedia && window.matchMedia("(pointer: coarse)").matches);

  function showReveal(el) {
    el.classList.add("is-visible");
  }

  if (revealEls.length && forceReveal) {
    revealEls.forEach(showReveal);
  } else if (revealEls.length && "IntersectionObserver" in window) {
    const io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            showReveal(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: "0px 0px -10px 0px" }
    );
    revealEls.forEach(function (el) {
      io.observe(el);
    });
    // Safety: never leave content hidden if IO misses
    window.setTimeout(function () {
      revealEls.forEach(showReveal);
    }, 1800);
  } else {
    revealEls.forEach(showReveal);
  }

  /* ----------------------------------------------------------------------
     Hero bubbles, splashes & ripples
     ---------------------------------------------------------------------- */
  const bubbleColors = ["aqua", "mint", "lavender", "amber", "coral"];

  function spawnBubbles(host, count) {
    if (!host) return;
    for (let i = 0; i < count; i++) {
      const b = document.createElement("span");
      const color = bubbleColors[i % bubbleColors.length];
      b.className = "bubble bubble--" + color;
      const size = 6 + Math.random() * 32;
      b.style.width = size + "px";
      b.style.height = size + "px";
      b.style.left = Math.random() * 100 + "%";
      b.style.bottom = -20 - Math.random() * 100 + "px";
      b.style.animationDuration = 6 + Math.random() * 14 + "s";
      b.style.animationDelay = Math.random() * 12 + "s";
      host.appendChild(b);
    }
  }

  function startDecorations() {
    if (prefersReducedMotion || startDecorations.done) return;
    startDecorations.done = true;
    spawnBubbles(document.getElementById("hero-bubbles"), 48);
    spawnBubbles(document.getElementById("footer-bubbles"), 16);

    const splashHost = document.getElementById("hero-splashes");
    if (splashHost) {
      const splashColors = ["", "mint", "amber", "lavender"];
      for (let i = 0; i < 16; i++) {
        const s = document.createElement("span");
        const c = splashColors[i % splashColors.length];
        s.className = "splash" + (c ? " splash--" + c : "");
        s.style.left = 4 + Math.random() * 92 + "%";
        s.style.top = 10 + Math.random() * 75 + "%";
        s.style.animationDelay = Math.random() * 5 + "s";
        s.style.animationDuration = 2.4 + Math.random() * 2.8 + "s";
        const size = 36 + Math.random() * 40;
        s.style.width = size + "px";
        s.style.height = size + "px";
        splashHost.appendChild(s);
      }
    }

    const rippleHost = document.getElementById("hero-ripples");
    if (rippleHost) {
      const rippleColors = ["", "amber", "lavender"];
      for (let i = 0; i < 10; i++) {
        const r = document.createElement("span");
        const c = rippleColors[i % rippleColors.length];
        r.className = "ripple" + (c ? " ripple--" + c : "");
        r.style.left = 8 + Math.random() * 84 + "%";
        r.style.top = 15 + Math.random() * 65 + "%";
        r.style.animationDelay = Math.random() * 5 + "s";
        r.style.animationDuration = 3.5 + Math.random() * 3.5 + "s";
        rippleHost.appendChild(r);
      }
    }

    /* Section floating droplets + bubble fields */
    document
      .querySelectorAll(
        "#how-it-works, #benefits, #ingredients, #adventures, .signup, #our-spray"
      )
      .forEach(function (section) {
        const cs = window.getComputedStyle(section);
        if (cs.position === "static") {
          section.style.position = "relative";
        }

        const field = document.createElement("div");
        field.className = "section-bubbles bubbles";
        field.setAttribute("aria-hidden", "true");
        section.insertBefore(field, section.firstChild);
        spawnBubbles(field, 10);

        for (let i = 0; i < 5; i++) {
          const d = document.createElement("span");
          d.className = "float-droplet";
          d.setAttribute("aria-hidden", "true");
          d.style.left = 5 + Math.random() * 90 + "%";
          d.style.top = 8 + Math.random() * 80 + "%";
          d.style.animationDelay = Math.random() * 4 + "s";
          d.style.animationDuration = 4.5 + Math.random() * 5 + "s";
          d.style.opacity = String(0.22 + Math.random() * 0.3);
          section.appendChild(d);
        }
      });

    /* Click / tap splash bursts */
    document.addEventListener(
      "click",
      function (e) {
        if (e.target.closest("a, button, input, select, textarea, label")) {
          const burst = document.createElement("span");
          burst.className = "click-splash";
          burst.style.left = e.clientX - 6 + "px";
          burst.style.top = e.clientY - 6 + "px";
          document.body.appendChild(burst);
          window.setTimeout(function () {
            burst.remove();
          }, 700);
        }
      },
      true
    );
  }
  window.addEventListener("load", function () {
    window.setTimeout(startDecorations, 1200);
  });

  /* ----------------------------------------------------------------------
     Soft parallax on lifestyle media (not hero — keeps float animation)
     ---------------------------------------------------------------------- */
  if (!prefersReducedMotion) {
    const parallaxTargets = document.querySelectorAll(
      ".product-intro__media, .chlorine-care__media"
    );
    parallaxTargets.forEach(function (el) {
      el.setAttribute("data-parallax", "1");
    });

    let ticking = false;
    window.addEventListener(
      "scroll",
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          const vh = window.innerHeight;
          parallaxTargets.forEach(function (el) {
            const rect = el.getBoundingClientRect();
            if (rect.bottom < 0 || rect.top > vh) return;
            const mid = rect.top + rect.height / 2;
            const offset = ((mid - vh / 2) / vh) * -18;
            el.style.transform = "translateY(" + offset.toFixed(1) + "px)";
          });
          ticking = false;
        });
      },
      { passive: true }
    );
  }

  /* ----------------------------------------------------------------------
     Magnetic hover on primary CTAs
     ---------------------------------------------------------------------- */
  if (!prefersReducedMotion && window.matchMedia("(pointer: fine)").matches) {
    document.querySelectorAll(".btn--primary, .btn--accent").forEach(function (btn) {
      btn.addEventListener("pointermove", function (e) {
        const r = btn.getBoundingClientRect();
        const x = e.clientX - r.left - r.width / 2;
        const y = e.clientY - r.top - r.height / 2;
        btn.style.transform =
          "translate(" +
          (x * 0.12).toFixed(1) +
          "px, " +
          (y * 0.18 - 3).toFixed(1) +
          "px) scale(1.02)";
      });
      btn.addEventListener("pointerleave", function () {
        btn.style.transform = "";
      });
    });
  }

  /* ----------------------------------------------------------------------
     Stagger card delays (works with existing .reveal classes)
     ---------------------------------------------------------------------- */
  if (!prefersReducedMotion) {
    document
      .querySelectorAll(
        ".steps, .benefits-grid, .ingredients-grid, .adventure-cards"
      )
      .forEach(function (grid) {
        Array.prototype.forEach.call(grid.children, function (child, i) {
          child.style.transitionDelay = 0.08 + i * 0.09 + "s";
        });
      });
  }

  /* ----------------------------------------------------------------------
     Form helpers
     ---------------------------------------------------------------------- */
  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  function setError(input, errorEl, message) {
    if (input) {
      input.classList.toggle("is-invalid", Boolean(message));
      input.setAttribute("aria-invalid", message ? "true" : "false");
    }
    if (errorEl) errorEl.textContent = message || "";
  }

  /* Email signup */
  const signupForm = document.getElementById("signup-form");
  const signupSuccess = document.getElementById("signup-success");

  if (signupForm) {
    signupForm.addEventListener("submit", function (e) {
      // Real submit handled by launch-fixes.php (AJAX) when present.
      if (window.swimfreshLaunch && window.swimfreshLaunch.ajaxUrl) { return; }
      e.preventDefault();
      const name = document.getElementById("signup-name");
      const email = document.getElementById("signup-email");
      const consent = document.getElementById("signup-consent");
      const nameErr = document.getElementById("signup-name-error");
      const emailErr = document.getElementById("signup-email-error");
      const consentErr = document.getElementById("signup-consent-error");

      let ok = true;

      if (!name || !name.value.trim()) {
        setError(name, nameErr, "Please enter your name.");
        ok = false;
      } else {
        setError(name, nameErr, "");
      }

      if (!email || !email.value.trim()) {
        setError(email, emailErr, "Please enter your email.");
        ok = false;
      } else if (!isValidEmail(email.value.trim())) {
        setError(email, emailErr, "Please enter a valid email address.");
        ok = false;
      } else {
        setError(email, emailErr, "");
      }

      if (!consent || !consent.checked) {
        if (consentErr) {
          consentErr.textContent = "Please confirm consent to continue.";
        }
        ok = false;
      } else if (consentErr) {
        consentErr.textContent = "";
      }

      if (!ok) return;

      // Front-end demo success — wire to email service / Shopify before launch
      signupForm.hidden = true;
      if (signupSuccess) {
        signupSuccess.classList.add("is-visible");
      }
});
  }

  /* Contact form */
  const contactForm = document.getElementById("contact-form");
  const contactSuccess = document.getElementById("contact-success");

  if (contactForm) {
    contactForm.addEventListener("submit", function (e) {
      e.preventDefault();
      const name = document.getElementById("contact-name");
      const email = document.getElementById("contact-email");
      const message = document.getElementById("contact-message");
      const nameErr = document.getElementById("contact-name-error");
      const emailErr = document.getElementById("contact-email-error");
      const messageErr = document.getElementById("contact-message-error");

      let ok = true;

      if (!name || !name.value.trim()) {
        setError(name, nameErr, "Please enter your name.");
        ok = false;
      } else {
        setError(name, nameErr, "");
      }

      if (!email || !email.value.trim() || !isValidEmail(email.value.trim())) {
        setError(email, emailErr, "Please enter a valid email.");
        ok = false;
      } else {
        setError(email, emailErr, "");
      }

      if (!message || !message.value.trim()) {
        setError(message, messageErr, "Please enter a message.");
        ok = false;
      } else {
        setError(message, messageErr, "");
      }

      if (!ok) return;

      if (contactSuccess) {
        contactSuccess.classList.add("is-visible");
      }
contactForm.reset();
    });
  }

  /* ----------------------------------------------------------------------
     Dynamic copyright year
     ---------------------------------------------------------------------- */
  const yearEl = document.getElementById("copyright-year");
  if (yearEl) {
    yearEl.textContent = String(new Date().getFullYear());
  }

  /* ----------------------------------------------------------------------
     Fun floaters (kid-friendly emoji shapes — sparse)
     ---------------------------------------------------------------------- */
  if (!prefersReducedMotion) {
    const floaterEmojis = ["💧", "✨", "🌊", "☀️", "🫧", "⭐"];
    document
      .querySelectorAll("#how-it-works, #benefits, #adventures, .signup")
      .forEach(function (section) {
        const cs = window.getComputedStyle(section);
        if (cs.position === "static") section.style.position = "relative";
        for (let i = 0; i < 4; i++) {
          const f = document.createElement("span");
          f.className = "fun-floater";
          f.setAttribute("aria-hidden", "true");
          f.textContent = floaterEmojis[(i + section.id.length) % floaterEmojis.length];
          f.style.left = 4 + Math.random() * 90 + "%";
          f.style.top = 8 + Math.random() * 75 + "%";
          f.style.animationDelay = Math.random() * 3 + "s";
          f.style.animationDuration = 5 + Math.random() * 5 + "s";
          f.style.fontSize = 1.1 + Math.random() * 0.7 + "rem";
          section.appendChild(f);
        }
      });
  }

  /* ----------------------------------------------------------------------
     Confetti on Add to Basket (fun for kids energy)
     ---------------------------------------------------------------------- */
  const addBasket = document.getElementById("add-to-basket");
  if (addBasket && !prefersReducedMotion) {
    addBasket.addEventListener("click", function (e) {
      const colors = ["#2ec4b6", "#f5a623", "#b8c9e8", "#ff6b6b", "#a8e6cf", "#ffd166"];
      const rect = addBasket.getBoundingClientRect();
      const originX = rect.left + rect.width / 2;
      const originY = rect.top + rect.height / 2;
      for (let i = 0; i < 18; i++) {
        const p = document.createElement("span");
        p.className = "confetti-piece";
        p.style.left = originX + "px";
        p.style.top = originY + "px";
        p.style.background = colors[i % colors.length];
        const angle = (Math.PI * 2 * i) / 18;
        const dist = 60 + Math.random() * 80;
        p.style.setProperty("--cx", Math.cos(angle) * dist + "px");
        p.style.setProperty("--cy", Math.sin(angle) * dist - 40 + "px");
        document.body.appendChild(p);
        window.setTimeout(function () {
          p.remove();
        }, 950);
      }
    });
  }

  /* ----------------------------------------------------------------------
     Active nav link on scroll
     ---------------------------------------------------------------------- */
  const sections = document.querySelectorAll("main section[id]");
  const navLinks = document.querySelectorAll(".nav-desktop .nav-link");

  if (sections.length && navLinks.length && "IntersectionObserver" in window) {
    const sectionIo = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          const id = entry.target.getAttribute("id");
          navLinks.forEach(function (link) {
            const href = link.getAttribute("href");
            link.classList.toggle("is-active", href === "#" + id);
          });
        });
      },
      { rootMargin: "-40% 0px -50% 0px", threshold: 0 }
    );
    sections.forEach(function (sec) {
      sectionIo.observe(sec);
    });
  }
})();
